import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import type {
  ChatMessage,
  ClientIdentity,
  Intention,
  IntentionStep,
  PublishedMedia,
  RoomSnapshot,
  UserProfile,
} from '../../shared/types'

export function useRoom(identity: ClientIdentity | null, preview = false) {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [connected, setConnected] = useState(false)
  const [users, setUsers] = useState<UserProfile[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [intentions, setIntentions] = useState<Intention[]>([])
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!identity) return
    const nextSocket = io({
      auth: preview ? { preview: true, identity } : {},
      transports: ['websocket', 'polling'],
    })
    socketRef.current = nextSocket
    setSocket(nextSocket)

    nextSocket.on('connect', () => setConnected(true))
    nextSocket.on('disconnect', () => setConnected(false))
    nextSocket.on('room:snapshot', (snapshot: RoomSnapshot) => {
      setUsers(snapshot.users)
      setMessages(snapshot.messages)
      setIntentions(snapshot.intentions)
    })
    nextSocket.on('presence:update', setUsers)
    nextSocket.on('chat:new', (message: ChatMessage) => {
      setMessages((current) => [...current, message].slice(-100))
    })
    nextSocket.on('chat:updated', (message: ChatMessage) => {
      setMessages((current) =>
        current.map((item) => (item.id === message.id ? message : item)),
      )
    })
    nextSocket.on('intention:new', (intention: Intention) => {
      setIntentions((current) => [intention, ...current])
    })
    nextSocket.on('intention:updated', (intention: Intention) => {
      // Upsert: an intention that just turned public is new to everyone else.
      setIntentions((current) =>
        current.some((item) => item.id === intention.id)
          ? current.map((item) => (item.id === intention.id ? intention : item))
          : [intention, ...current],
      )
    })
    nextSocket.on('intention:removed', ({ id }: { id: string }) => {
      setIntentions((current) => current.filter((item) => item.id !== id))
    })

    return () => {
      nextSocket.disconnect()
      socketRef.current = null
      setSocket(null)
    }
  }, [identity?.id, preview])

  const sendMessage = useCallback((body: string) => {
    socketRef.current?.emit('chat:send', { body })
  }, [])

  const reactToMessage = useCallback((messageId: string, emoji: string) => {
    socketRef.current?.emit('chat:react', { messageId, emoji })
  }, [])

  const createIntention = useCallback(
    (input: {
      title: string
      steps: Pick<IntentionStep, 'text'>[]
      isPublic: boolean
      durationMinutes?: number
    }) => socketRef.current?.emit('intention:create', input),
    [],
  )

  const updateIntention = useCallback(
    (input: {
      intentionId: string
      status?: 'in_progress' | 'completed'
      stepId?: string
      title?: string
      steps?: Pick<IntentionStep, 'text'>[]
      isPublic?: boolean
      durationMinutes?: number | null
    }) => socketRef.current?.emit('intention:update', input),
    [],
  )

  const reactToIntention = useCallback((intentionId: string, emoji: string) => {
    socketRef.current?.emit('intention:react', { intentionId, emoji })
  }, [])

  const deleteIntention = useCallback((intentionId: string) => {
    socketRef.current?.emit('intention:delete', { intentionId })
  }, [])

  const reportUser = useCallback((userId: string) => {
    socketRef.current?.emit('user:report', { userId })
  }, [])

  const updatePresence = useCallback(
    (patch: { cameraOn?: boolean; media?: PublishedMedia | null }) =>
      socketRef.current?.emit('presence:update', patch),
    [],
  )

  const updateProfile = useCallback((profile: Omit<ClientIdentity, 'id' | 'color'>) => {
    socketRef.current?.emit('profile:update', profile)
  }, [])

  return {
    socket,
    connected,
    users,
    messages,
    intentions,
    sendMessage,
    reactToMessage,
    createIntention,
    updateIntention,
    reactToIntention,
    deleteIntention,
    reportUser,
    updatePresence,
    updateProfile,
  }
}
